// Advanced Capabilities Analysis connecting Slider Levels to Actual Capabilities
import { dimensionsData } from '../data/ValueDimensions';
import { sliderLabels } from '../data/sliderLabels';

export const AdvancedCapabilitiesAnalysis = {
  analyzeScenarioCapabilities: (scenarios, categories, industry, tier) => {
    const analysis = {
      capabilitiesMatrix: generateCapabilitiesMatrix(scenarios, categories),
      capabilityComparison: compareScenarioCapabilities(scenarios, categories),
      maturityAssessment: assessMaturityLevels(scenarios, categories),
      gapAnalysis: performAdvancedGapAnalysis(scenarios, categories),
      strategicRecommendations: generateStrategicRecommendations(scenarios, categories, industry, tier)
    };

    return analysis;
  },

  getScenarioCapabilities: (scenario, categories) => {
    return extractCapabilitiesFromScenario(scenario, categories);
  },

  compareCapabilitiesMatrix: (scenarios, categories) => {
    return generateDetailedComparisonMatrix(scenarios, categories);
  }
};

// CORRECTED: Extract capabilities using the same approach as PerformanceDimensionsRadar
const extractCapabilitiesFromScenario = (scenario, categories) => {
  const capabilities = {
    currentCapabilities: [],
    targetCapabilities: [],
    newCapabilities: [],
    enhancedCapabilities: [],
    capabilityGaps: []
  };

  // Analyze each factor in the scenario
  categories.forEach(category => {
    category.factors.forEach(factor => {
      const factorKey = factor.key;
      const factorData = scenario.values?.[factorKey];
      
      if (factorData && dimensionsData[factorKey]) {
        const currentLevel = Math.max(0, Math.min(10, factorData.current || 0));
        const targetLevel = Math.max(0, Math.min(10, factorData.target || 0));
        
        // CORRECTED: Get capabilities using the same method as PerformanceDimensionsRadar
        const currentCapabilities = getPLMCapabilitiesForLevel(factorKey, currentLevel);
        const targetCapabilities = getPLMCapabilitiesForLevel(factorKey, targetLevel);
        
        // Get dimension values for maturity scoring
        const currentDimensionValues = getDimensionValues(factorKey, currentLevel);
        const targetDimensionValues = getDimensionValues(factorKey, targetLevel);

        // Current capabilities with maturity scores
        capabilities.currentCapabilities.push({
          category: category.name,
          factor: factor.label,
          factorKey: factorKey,
          level: currentLevel,
          levelLabel: sliderLabels[factorKey]?.[currentLevel] || `Level ${currentLevel}`,
          capabilities: currentCapabilities,
          dimensionValues: currentDimensionValues,
          maturityScore: calculateMaturityScore(currentDimensionValues),
          impact: calculateCapabilityImpact(factorKey, currentLevel)
        });

        // Target capabilities with maturity scores
        capabilities.targetCapabilities.push({
          category: category.name,
          factor: factor.label,
          factorKey: factorKey,
          level: targetLevel,
          levelLabel: sliderLabels[factorKey]?.[targetLevel] || `Level ${targetLevel}`,
          capabilities: targetCapabilities,
          dimensionValues: targetDimensionValues,
          maturityScore: calculateMaturityScore(targetDimensionValues),
          impact: calculateCapabilityImpact(factorKey, targetLevel)
        });

        // New capabilities (only in target, not in current)
        const newCaps = targetCapabilities.filter(targetCap => 
          !currentCapabilities.some(currentCap => currentCap === targetCap)
        );
        
        if (newCaps.length > 0) {
          capabilities.newCapabilities.push({
            category: category.name,
            factor: factor.label,
            capabilities: newCaps,
            businessValue: calculateBusinessValue(newCaps, factorKey),
            implementationComplexity: estimateImplementationComplexity(factorKey, currentLevel, targetLevel)
          });
        }

        // Enhanced capabilities (present in both but level increased)
        if (targetLevel > currentLevel) {
          capabilities.enhancedCapabilities.push({
            category: category.name,
            factor: factor.label,
            levelImprovement: targetLevel - currentLevel,
            maturityImprovement: calculateMaturityScore(targetDimensionValues) - calculateMaturityScore(currentDimensionValues),
            businessImpact: calculateCapabilityImpact(factorKey, targetLevel) - calculateCapabilityImpact(factorKey, currentLevel)
          });
        }
      }
    });
  });

  return capabilities;
};

// CORRECTED: Use the EXACT same method as PerformanceDimensionsRadar component
const getPLMCapabilitiesForLevel = (factorKey, level) => {
  try {
    const safeLevel = Math.max(0, Math.min(10, parseInt(level) || 0));
    
    if (!dimensionsData || !dimensionsData[factorKey]) {
      return ['Basic PLM Features'];
    }
    
    const factorData = dimensionsData[factorKey];
    const orderedLevels = Object.keys(factorData).sort((a, b) => parseInt(a) - parseInt(b));
    
    if (!orderedLevels.length) {
      return ['Standard PLM Capabilities'];
    }
    
    const levelIndex = Math.min(Math.max(safeLevel, 0), orderedLevels.length - 1);
    const levelKey = orderedLevels[levelIndex];
    
    if (!levelKey || !factorData[levelKey]) {
      return ['Core PLM Functions'];
    }
    
    const capabilities = factorData[levelKey].PLMCapabilities;
    return Array.isArray(capabilities) ? capabilities : ['Advanced PLM Tools'];
    
  } catch (error) {
    console.error(`Error getting PLM capabilities for ${factorKey}:`, error);
    return ['Essential PLM Features'];
  }
};

// CORRECTED: Use the EXACT same method as PerformanceDimensionsRadar component
const getDimensionValues = (factorKey, level) => {
  try {
    const safeLevel = Math.max(0, Math.min(10, parseInt(level) || 0));
    const defaultValues = [50, 50, 50, 50, 50, 50, 50, 50];
    
    if (!dimensionsData || !dimensionsData[factorKey]) {
      return defaultValues;
    }
    
    const factorData = dimensionsData[factorKey];
    const orderedLevels = Object.keys(factorData).sort((a, b) => parseInt(a) - parseInt(b));
    
    if (!orderedLevels.length) {
      return defaultValues;
    }
    
    const levelIndex = Math.min(Math.max(safeLevel, 0), orderedLevels.length - 1);
    const levelKey = orderedLevels[levelIndex];
    
    if (!levelKey || !factorData[levelKey]) {
      return defaultValues;
    }
    
    const levelData = factorData[levelKey];
    
    // CORRECTED: Use the same dimension mapping as PerformanceDimensionsRadar
    return [
      levelData.OperationalEfficiency || safeLevel * 10,
      levelData.CostReduction || safeLevel * 10,
      levelData.TimeToMarket || safeLevel * 10,
      levelData.InnovationCapacity || safeLevel * 10,
      levelData.ComplianceRisk || safeLevel * 10,
      levelData.RevenueGrowth || safeLevel * 10,
      levelData.Sustainability || safeLevel * 10,
      levelData.SupplyChainResilience || safeLevel * 10
    ].map(val => Math.min(100, Math.max(0, Number(val) || 50)));
    
  } catch (error) {
    console.error(`Error getting dimension values for ${factorKey}:`, error);
    return [50, 50, 50, 50, 50, 50, 50, 50];
  }
};

// Generate comprehensive capabilities matrix
const generateCapabilitiesMatrix = (scenarios, categories) => {
  const matrix = {
    scenarios: [],
    capabilities: new Set(),
    data: [],
    summary: {}
  };

  // Get all unique capabilities across all scenarios
  scenarios.forEach((scenario, scenarioIndex) => {
    const scenarioCaps = extractCapabilitiesFromScenario(scenario, categories);
    
    // Add scenario to matrix
    matrix.scenarios.push({
      id: scenarioIndex,
      name: scenario.name || `Scenario ${scenarioIndex + 1}`,
      capabilities: scenarioCaps
    });

    // CORRECTED: Collect all unique capabilities from target capabilities
    scenarioCaps.targetCapabilities.forEach(item => {
      item.capabilities.forEach(cap => {
        matrix.capabilities.add(cap);
      });
    });
  });

  // Convert capabilities set to array
  matrix.capabilities = Array.from(matrix.capabilities);

  // Create matrix data
  matrix.data = matrix.scenarios.map(scenario => {
    const scenarioCapabilities = {};
    
    matrix.capabilities.forEach(capability => {
      // Check if scenario has this capability
      const hasCapability = scenario.capabilities.targetCapabilities.some(item =>
        item.capabilities.includes(capability)
      );
      
      if (hasCapability) {
        // Get capability details
        const capItem = scenario.capabilities.targetCapabilities.find(item =>
          item.capabilities.includes(capability)
        );
        
        scenarioCapabilities[capability] = {
          present: true,
          level: capItem?.level || 0,
          maturityScore: capItem?.maturityScore || 0,
          category: capItem?.category || 'General',
          factor: capItem?.factor || 'Unknown'
        };
      } else {
        scenarioCapabilities[capability] = {
          present: false,
          level: 0,
          maturityScore: 0,
          category: 'General',
          factor: 'Unknown'
        };
      }
    });
    
    return scenarioCapabilities;
  });

  // Generate summary statistics
  matrix.summary = generateMatrixSummary(matrix);

  return matrix;
};

// Compare scenario capabilities
const compareScenarioCapabilities = (scenarios, categories) => {
  const comparison = {
    overallMaturity: {},
    capabilityCoverage: {},
    strategicAlignment: {}
  };

  scenarios.forEach((scenario, index) => {
    const capabilities = extractCapabilitiesFromScenario(scenario, categories);
    
    comparison.overallMaturity[`scenario_${index}`] = calculateOverallMaturity(capabilities);
    comparison.capabilityCoverage[`scenario_${index}`] = calculateCapabilityCoverage(capabilities);
    comparison.strategicAlignment[`scenario_${index}`] = calculateStrategicAlignment(capabilities);
  });

  return comparison;
};

// Generate detailed comparison matrix
const generateDetailedComparisonMatrix = (scenarios, categories) => {
  const comparison = {
    headers: ['Capability', 'Category', 'Factor', ...scenarios.map((_, i) => `Scenario ${i + 1}`)],
    rows: [],
    metrics: {}
  };

  // Get all capabilities across all scenarios
  const allCapabilities = new Map();

  scenarios.forEach((scenario, scenarioIndex) => {
    const capabilities = extractCapabilitiesFromScenario(scenario, categories);
    
    capabilities.targetCapabilities.forEach(item => {
      item.capabilities.forEach(cap => {
        const key = `${cap}|${item.category}|${item.factor}`;
        if (!allCapabilities.has(key)) {
          allCapabilities.set(key, {
            name: cap,
            category: item.category,
            factor: item.factor,
            scenarios: new Array(scenarios.length).fill(null)
          });
        }
        
        const capability = allCapabilities.get(key);
        capability.scenarios[scenarioIndex] = {
          level: item.level,
          maturityScore: item.maturityScore,
          present: true
        };
      });
    });
  });

  // Convert to rows
  comparison.rows = Array.from(allCapabilities.values()).map(cap => [
    cap.name,
    cap.category,
    cap.factor,
    ...cap.scenarios.map(scenarioCap => 
      scenarioCap ? 
        `Level ${scenarioCap.level} (Maturity: ${scenarioCap.maturityScore.toFixed(1)})` : 
        'Not Available'
    )
  ]);

  // Calculate metrics
  comparison.metrics = calculateComparisonMetrics(allCapabilities, scenarios.length);

  return comparison;
};

// Advanced gap analysis
const performAdvancedGapAnalysis = (scenarios, categories) => {
  const gaps = {
    capabilityGaps: [],
    maturityGaps: [],
    strategicGaps: [],
    quickWins: []
  };

  if (scenarios.length < 2) return gaps;

  // Compare each scenario against others
  const baseScenario = scenarios[0];
  const baseCapabilities = extractCapabilitiesFromScenario(baseScenario, categories);

  scenarios.slice(1).forEach((scenario, index) => {
    const compareCapabilities = extractCapabilitiesFromScenario(scenario, categories);
    
    // Find capabilities present in comparison but missing in base
    compareCapabilities.targetCapabilities.forEach(compareItem => {
      compareItem.capabilities.forEach(compareCap => {
        const inBase = baseCapabilities.targetCapabilities.some(baseItem =>
          baseItem.capabilities.some(baseCap => baseCap === compareCap)
        );

        if (!inBase) {
          gaps.capabilityGaps.push({
            capability: compareCap,
            category: compareItem.category,
            scenario: `Scenario ${index + 2}`,
            level: compareItem.level,
            businessImpact: estimateBusinessImpact(compareCap, compareItem.category),
            implementationEffort: estimateImplementationEffort(compareCap)
          });
        }
      });
    });

    // Find maturity differences
    baseCapabilities.targetCapabilities.forEach(baseItem => {
      baseItem.capabilities.forEach(baseCap => {
        const compareItem = compareCapabilities.targetCapabilities.find(item =>
          item.capabilities.includes(baseCap)
        );

        if (compareItem && compareItem.level > baseItem.level) {
          gaps.maturityGaps.push({
            capability: baseCap,
            category: baseItem.category,
            baseLevel: baseItem.level,
            compareLevel: compareItem.level,
            difference: compareItem.level - baseItem.level,
            improvementPotential: calculateImprovementPotential(baseItem, compareItem)
          });
        }
      });
    });
  });

  return gaps;
};

// Helper functions
const calculateMaturityScore = (dimensionValues) => {
  if (!dimensionValues || dimensionValues.length === 0) return 0;
  return (dimensionValues.reduce((sum, val) => sum + val, 0) / dimensionValues.length);
};

const calculateOverallMaturity = (capabilities) => {
  const allScores = capabilities.targetCapabilities.map(item => item.maturityScore);
  return allScores.length > 0 ? allScores.reduce((sum, score) => sum + score, 0) / allScores.length : 0;
};

const calculateCapabilityCoverage = (capabilities) => {
  const totalPossible = 100; // Estimated total possible capabilities
  const actualCount = capabilities.targetCapabilities.reduce((sum, item) => sum + item.capabilities.length, 0);
  
  return {
    coverage: (actualCount / totalPossible) * 100,
    count: actualCount
  };
};

const calculateStrategicAlignment = (capabilities) => {
  return capabilities.targetCapabilities.reduce((sum, item) => sum + item.maturityScore, 0) / Math.max(1, capabilities.targetCapabilities.length);
};

const calculateCapabilityImpact = (factorKey, level) => {
  return level * 1000;
};

const calculateBusinessValue = (capabilities, factorKey) => {
  return capabilities.length * 5000;
};

const estimateImplementationComplexity = (factorKey, currentLevel, targetLevel) => {
  return Math.min(10, Math.max(1, (targetLevel - currentLevel) * 2));
};

const estimateBusinessImpact = (capability, category) => {
  let baseImpact = 50000;
  if (capability.includes('AI') || capability.includes('Predictive')) baseImpact *= 2;
  if (capability.includes('Automation') || capability.includes('Optimization')) baseImpact *= 1.5;
  return baseImpact;
};

const estimateImplementationEffort = (capability) => {
  let effort = 5;
  if (capability.includes('AI') || capability.includes('Integration')) effort += 3;
  if (capability.includes('Automation') || capability.includes('Analytics')) effort += 2;
  return Math.min(10, effort);
};

const calculateImprovementPotential = (baseItem, compareItem) => {
  const levelDifference = compareItem.level - baseItem.level;
  const maturityDifference = compareItem.maturityScore - baseItem.maturityScore;
  
  return {
    levelImprovement: levelDifference,
    maturityImprovement: maturityDifference,
    overallPotential: (levelDifference * 0.6) + (maturityDifference * 0.4)
  };
};

const generateMatrixSummary = (matrix) => {
  const summary = {
    totalCapabilities: matrix.capabilities.length,
    scenarioCoverage: {},
    categoryBreakdown: {}
  };

  // Calculate coverage per scenario
  matrix.scenarios.forEach((scenario) => {
    const presentCapabilities = Object.values(matrix.data[scenario.id]).filter(cap => cap.present).length;
    summary.scenarioCoverage[scenario.name] = {
      coverage: (presentCapabilities / matrix.capabilities.length) * 100,
      count: presentCapabilities
    };
  });

  return summary;
};

const calculateComparisonMetrics = (capabilitiesMap, scenarioCount) => {
  const metrics = {
    uniqueCapabilities: capabilitiesMap.size,
    averageCapabilitiesPerScenario: 0,
    mostCommonCapabilities: []
  };

  let totalCapabilities = 0;
  const capabilityCounts = new Map();

  capabilitiesMap.forEach((capability) => {
    const presentCount = capability.scenarios.filter(sc => sc && sc.present).length;
    totalCapabilities += presentCount;
    
    if (!capabilityCounts.has(capability.name)) {
      capabilityCounts.set(capability.name, 0);
    }
    capabilityCounts.set(capability.name, capabilityCounts.get(capability.name) + presentCount);
  });

  metrics.averageCapabilitiesPerScenario = totalCapabilities / scenarioCount;

  // Get top 5 most common capabilities
  metrics.mostCommonCapabilities = Array.from(capabilityCounts.entries())
    .sort((a, b) => b[1] - a[1])
    .slice(0, 5)
    .map(([name, count]) => ({ name, count }));

  return metrics;
};

// Strategic recommendations functions
const generateStrategicRecommendations = (scenarios, categories, industry, tier) => {
  const recommendations = {
    scenarioSpecific: {},
    crossScenario: []
  };

  scenarios.forEach((scenario, index) => {
    const capabilities = extractCapabilitiesFromScenario(scenario, categories);
    
    recommendations.scenarioSpecific[`scenario_${index}`] = {
      strengths: identifyStrengths(capabilities),
      weaknesses: identifyWeaknesses(capabilities),
      opportunities: identifyOpportunities(capabilities, industry),
      recommendations: generateScenarioRecommendations(capabilities, industry, tier)
    };
  });

  recommendations.crossScenario = generateCrossScenarioRecommendations(recommendations.scenarioSpecific);

  return recommendations;
};

const identifyStrengths = (capabilities) => {
  return capabilities.targetCapabilities
    .filter(item => item.maturityScore >= 70)
    .map(item => ({
      capability: item.capabilities[0] || 'General Capability',
      category: item.category,
      maturityScore: item.maturityScore,
      level: item.level
    }));
};

const identifyWeaknesses = (capabilities) => {
  return capabilities.targetCapabilities
    .filter(item => item.maturityScore < 50)
    .map(item => ({
      capability: item.capabilities[0] || 'General Capability',
      category: item.category,
      maturityScore: item.maturityScore,
      level: item.level,
      issue: 'Low maturity capability'
    }));
};

const identifyOpportunities = (capabilities, industry) => {
  const industryTrends = {
    automotive: ['Sustainability', 'Digital Twin', 'Supply Chain Resilience'],
    aerospace: ['Predictive Maintenance', 'Advanced Materials', 'Quality Management'],
    electronics: ['AI Integration', 'Rapid Prototyping', 'Supply Chain Optimization']
  };

  const trends = industryTrends[industry] || [];
  const opportunities = capabilities.targetCapabilities.filter(item =>
    trends.some(trend => item.capabilities.some(cap => cap.includes(trend)))
  );

  return opportunities.map(item => ({
    capability: item.capabilities[0] || 'General Capability',
    category: item.category,
    trendAlignment: 'High',
    opportunity: `Aligns with ${industry} industry trend`
  }));
};

const generateScenarioRecommendations = (capabilities, industry, tier) => {
  const recommendations = [];
  
  const weaknesses = identifyWeaknesses(capabilities);
  const opportunities = identifyOpportunities(capabilities, industry);

  weaknesses.forEach(weakness => {
    recommendations.push({
      type: 'Improvement',
      priority: 'High',
      action: `Enhance ${weakness.capability} capability`,
      rationale: `Current maturity ${weakness.maturityScore.toFixed(1)}% is below optimal`,
      timeline: '3-6 months'
    });
  });

  opportunities.forEach(opportunity => {
    recommendations.push({
      type: 'Opportunity',
      priority: 'Medium',
      action: `Leverage ${opportunity.capability} for competitive advantage`,
      rationale: opportunity.opportunity,
      timeline: '6-12 months'
    });
  });

  return recommendations.slice(0, 5);
};

const generateCrossScenarioRecommendations = (scenarioSpecific) => {
  const recommendations = [];
  
  // Identify common weaknesses across scenarios
  const allWeaknesses = Object.values(scenarioSpecific).flatMap(s => s.weaknesses);
  const commonWeaknesses = allWeaknesses.filter((weakness, index, array) =>
    array.findIndex(w => w.capability === weakness.capability) !== index
  );

  const uniqueCommonWeaknesses = [...new Map(commonWeaknesses.map(item => [item.capability, item])).values()];

  uniqueCommonWeaknesses.forEach(weakness => {
    recommendations.push({
      type: 'Cross-Cutting',
      priority: 'High',
      action: `Address common weakness: ${weakness.capability}`,
      rationale: `This weakness appears in multiple scenarios`,
      impact: 'High'
    });
  });

  return recommendations;
};

const assessMaturityLevels = (scenarios, categories) => {
  const maturity = {
    individualMaturity: {},
    comparativeMaturity: {}
  };

  scenarios.forEach((scenario, index) => {
    const capabilities = extractCapabilitiesFromScenario(scenario, categories);
    
    maturity.individualMaturity[`scenario_${index}`] = {
      operationalMaturity: calculateDimensionMaturity(capabilities, 'Operational'),
      strategicMaturity: calculateDimensionMaturity(capabilities, 'Strategic'),
      innovationMaturity: calculateDimensionMaturity(capabilities, 'Innovation'),
      overallMaturity: calculateOverallMaturity(capabilities)
    };
  });

  return maturity;
};

const calculateDimensionMaturity = (capabilities, dimension) => {
  const dimensionItems = capabilities.targetCapabilities.filter(item =>
    item.category.includes(dimension)
  );
  
  if (dimensionItems.length === 0) return 0;
  
  return dimensionItems.reduce((sum, item) => sum + item.maturityScore, 0) / dimensionItems.length;
};

export default AdvancedCapabilitiesAnalysis;
